import React, { useEffect, useRef, useState } from "react";
import { Mic, Volume2, Loader2, Sparkles, Brain, CheckCircle2 } from "lucide-react";

/**
 * 3D Audio Equalizer — state-aware.
 * - 28 vertical bars rendered with CSS perspective for a true 3D feel
 * - Bars animate ONLY when the AI examiner is speaking (driven by audioLevel)
 * - Static (low + gentle breathing) when idle / listening / processing
 * - A state badge above the equalizer tells the user exactly what is happening:
 *     • "Examiner is speaking"
 *     • "Listening to your answer"
 *     • "Processing your answer"
 *     • "Generating band score"
 *     • "Ready to begin"
 *     • "Preparing your topic"
 */

const ROOM_IMG =
  "https://images.unsplash.com/photo-1497366754035-f200968a6e72?w=2000&q=80&auto=format&fit=crop";

const BAR_COUNT = 28;

function StateBadge({ stage, isSpeaking, recording, loading }) {
  let label = "Ready to begin";
  let Icon = CheckCircle2;
  let accent = "rgba(125,200,255,0.9)";
  let pulse = false;

  if (stage === "evaluating") {
    label = "Generating band score";
    Icon = Sparkles;
    accent = "#FB7185";
    pulse = true;
  } else if (loading && stage !== "examiner-speaking" && stage !== "welcome") {
    label = "Processing your answer";
    Icon = Brain;
    accent = "#FB7185";
    pulse = true;
  } else if (isSpeaking || stage === "examiner-speaking") {
    label = "Examiner is speaking";
    Icon = Volume2;
    accent = "#FB7185";
  } else if (stage === "prep") {
    label = "Prepare your topic";
    Icon = Sparkles;
    accent = "rgba(168,180,255,0.9)";
  } else if (stage === "listening") {
    label = recording ? "Recording your answer" : "Your turn to speak";
    Icon = Mic;
    accent = recording ? "#FB7185" : "rgba(125,200,255,0.9)";
    pulse = recording;
  } else if (stage === "result") {
    label = "Test complete";
    Icon = CheckCircle2;
    accent = "rgba(140,220,170,0.9)";
  }

  const dynamicTestId =
    pulse && (stage === "evaluating" || loading)
      ? "processing-indicator"
      : isSpeaking || stage === "examiner-speaking"
      ? "voice-equalizer"
      : "state-badge";

  return (
    <div
      className="px-5 py-2.5 rounded-full bg-black/55 backdrop-blur-md border flex items-center gap-3"
      style={{ borderColor: `${accent}44` }}
      data-testid={dynamicTestId}
      data-state-badge="true"
    >
      <div className="relative">
        {pulse && (
          <span
            className="absolute -inset-2 rounded-full"
            style={{
              background: accent,
              opacity: 0.25,
              animation: "badge-ping 1.4s ease-out infinite",
            }}
          />
        )}
        <Icon className="relative w-4 h-4" style={{ color: accent }} />
      </div>
      <span
        className="text-[11px] font-semibold uppercase tracking-[0.22em] text-white"
        data-testid="state-label"
      >
        {label}
      </span>
      {(loading || stage === "evaluating") && (
        <Loader2 className="w-3.5 h-3.5 text-white/70 animate-spin" />
      )}
    </div>
  );
}

export default function ExaminerRoom3D({
  audioLevel = 0,
  isSpeaking = false,
  stage = "welcome",
  recording = false,
  loading = false,
}) {
  const [tick, setTick] = useState(0);
  const rafRef = useRef(null);
  const tRef = useRef(0);

  // Active animation only while it's needed. Idle uses lower-FPS interval.
  useEffect(() => {
    const active = isSpeaking || stage === "evaluating" || loading || recording;
    if (active) {
      const loop = () => {
        tRef.current += 0.05;
        setTick((n) => (n + 1) % 100000);
        rafRef.current = requestAnimationFrame(loop);
      };
      rafRef.current = requestAnimationFrame(loop);
      return () => cancelAnimationFrame(rafRef.current);
    }
    // Idle: low-FPS gentle breathing
    const id = setInterval(() => {
      tRef.current += 0.08;
      setTick((n) => (n + 1) % 100000);
    }, 90);
    return () => clearInterval(id);
  }, [isSpeaking, stage, loading, recording]);

  const t = tRef.current;

  // Compute each bar's height based on current state.
  // Bars are mirrored from center outward (symmetry feels more "sound-equalizer-ish").
  const heights = Array.from({ length: BAR_COUNT }).map((_, i) => {
    const center = (BAR_COUNT - 1) / 2;
    const dist = Math.abs(i - center) / center; // 0 at center → 1 at edge
    const phase = i * 0.5;

    if (isSpeaking) {
      // Always-vivid speaking animation — even at zero audioLevel the bars dance.
      // Three superposed sine waves give a natural-looking spectrum.
      const w1 = Math.sin(t * 5.5 + phase) * 0.5 + 0.5;          // 0..1
      const w2 = Math.sin(t * 8.7 + phase * 1.9 + 1.3) * 0.5 + 0.5;
      const w3 = Math.sin(t * 3.1 + phase * 0.5 + 2.7) * 0.5 + 0.5;
      const combined = (w1 * 0.55 + w2 * 0.3 + w3 * 0.35);       // ~0..1
      const centerBoost = (1 - dist * 0.55) * 90;                // taller in the middle
      const audioBoost = audioLevel * (1 - dist * 0.5) * 220;    // big boost when voice loud
      const baseline = 18 + (1 - dist * 0.7) * 8;
      return Math.max(10, baseline + combined * (centerBoost + audioBoost));
    }
    if (stage === "evaluating" || (loading && stage !== "welcome")) {
      // Thinking wave: gentle travelling sine pulse
      const wave = Math.sin(t * 2.2 + i * 0.5) * 0.5 + 0.5;
      return 16 + wave * 26 + (1 - dist) * 10;
    }
    if (recording) {
      // Recording: bars react to STUDENT'S voice (mic-driven audioLevel)
      const w1 = Math.sin(t * 6 + phase) * 0.5 + 0.5;
      const w2 = Math.sin(t * 9 + phase * 1.4 + 0.8) * 0.5 + 0.5;
      const combined = w1 * 0.6 + w2 * 0.4;
      const audioBoost = audioLevel * (1 - dist * 0.5) * 200;
      const baseline = 12 + (1 - dist * 0.7) * 6;
      return Math.max(8, baseline + combined * (audioBoost + 18));
    }
    if (stage === "prep") {
      // Slow contemplative wave
      const wave = Math.sin(t * 1.2 + phase * 0.6) * 0.5 + 0.5;
      return 10 + wave * 12;
    }
    // Idle: low static bars with very gentle breath
    const breath = Math.sin(t * 0.8 + phase * 0.4) * 0.5 + 0.5;
    return 8 + breath * 4 + (1 - dist * 0.8) * 4;
  });

  // Color palette per state
  const isActive = isSpeaking;
  const isThinking = (stage === "evaluating" || (loading && stage !== "welcome"));
  const isRec = recording;
  const accentTop = isActive
    ? "#FB7185"
    : isThinking
    ? "#A78BFA"
    : isRec
    ? "#F87171"
    : "#7DD3FC";
  const accentMid = isActive
    ? "#F77F00"
    : isThinking
    ? "#7C3AED"
    : isRec
    ? "#DC2626"
    : "#0EA5E9";
  const accentDark = isActive
    ? "#831843"
    : isThinking
    ? "#3B0764"
    : isRec
    ? "#7F1D1D"
    : "#003049";

  return (
    <div
      className="relative w-full h-full overflow-hidden"
      data-testid="examiner-room-scene"
      style={{ backgroundColor: "#070b14" }}
    >
      {/* Real office backdrop — heavily dimmed for focus on equalizer */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `url(${ROOM_IMG})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          filter: "blur(14px) saturate(0.5) brightness(0.32)",
          transform: "scale(1.08)",
        }}
      />
      {/* Deep gradient overlay */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(180deg, rgba(7,11,20,0.45) 0%, rgba(7,11,20,0.7) 60%, rgba(7,11,20,0.95) 100%)",
        }}
      />

      {/* Ambient glow tinted by state */}
      <div
        className="absolute inset-0 pointer-events-none transition-opacity duration-700"
        style={{
          background: `radial-gradient(ellipse at center 60%, ${accentMid}22 0%, transparent 60%)`,
          opacity: isActive ? 1 : 0.4,
        }}
      />

      {/* ===== Center stage: 3D Equalizer ===== */}
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-10 pointer-events-none">
        {/* State badge */}
        <StateBadge
          stage={stage}
          isSpeaking={isSpeaking}
          recording={recording}
          loading={loading}
        />

        {/* 3D equalizer stage */}
        <div
          className="relative"
          style={{
            perspective: "900px",
            perspectiveOrigin: "50% 30%",
          }}
        >
          {/* Bars */}
          <div
            className="flex items-end gap-[6px] h-[260px]"
            style={{
              transform: "rotateX(18deg)",
              transformStyle: "preserve-3d",
              filter: `drop-shadow(0 18px 30px ${accentMid}55)`,
            }}
            data-testid="equalizer-bars"
          >
            {heights.map((h, i) => (
              <div
                key={i}
                className="relative"
                style={{
                  width: 12,
                  height: `${h}px`,
                  borderRadius: "8px 8px 4px 4px",
                  background: `linear-gradient(180deg, ${accentTop} 0%, ${accentMid} 55%, ${accentDark} 100%)`,
                  boxShadow: `inset 0 -8px 12px rgba(0,0,0,0.4), inset 0 4px 6px rgba(255,255,255,0.35), 0 0 14px ${accentMid}55`,
                  transition: "height 50ms cubic-bezier(0.4,0,0.2,1)",
                  transformStyle: "preserve-3d",
                }}
              >
                {/* Glossy top cap */}
                <div
                  className="absolute top-0 left-0 right-0 h-2 rounded-t-[8px]"
                  style={{
                    background:
                      "linear-gradient(180deg, rgba(255,255,255,0.7) 0%, rgba(255,255,255,0) 100%)",
                  }}
                />
              </div>
            ))}
          </div>

          {/* Reflective floor */}
          <div
            className="absolute left-0 right-0 top-full mt-1 flex items-start gap-[6px] h-[100px] overflow-hidden opacity-30"
            style={{
              transform: "rotateX(-160deg)",
              transformOrigin: "top",
              WebkitMaskImage:
                "linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0) 80%)",
              maskImage:
                "linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0) 80%)",
            }}
            aria-hidden
          >
            {heights.map((h, i) => (
              <div
                key={`r-${i}`}
                style={{
                  width: 12,
                  height: `${Math.min(80, h * 0.7)}px`,
                  borderRadius: "8px 8px 4px 4px",
                  background: `linear-gradient(180deg, ${accentTop} 0%, ${accentMid} 60%, transparent 100%)`,
                }}
              />
            ))}
          </div>

          {/* Stage glow line under bars */}
          <div
            className="absolute left-[-10%] right-[-10%] top-full mt-1 h-px"
            style={{
              background: `linear-gradient(90deg, transparent 0%, ${accentMid}cc 50%, transparent 100%)`,
              boxShadow: `0 0 16px ${accentMid}88`,
            }}
          />
        </div>

        {/* Helper hint below */}
        <div className="text-[11px] text-white/45 tracking-wider uppercase">
          {stage === "welcome" ? "Tap “Begin Test” when you are ready" : "\u00A0"}
        </div>
      </div>

      {/* Vignette */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at center, transparent 50%, rgba(0,0,0,0.55) 100%)",
        }}
      />

      <style>{`
        @keyframes badge-ping {
          0%   { transform: scale(0.9); opacity: 0.45; }
          80%  { transform: scale(1.6); opacity: 0; }
          100% { transform: scale(1.6); opacity: 0; }
        }
      `}</style>
    </div>
  );
}
